"use client";

import React, { useState, useMemo } from "react";
import { Search } from "lucide-react";
import { cn } from "../../lib/utils";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  useAcceptedCurrencies,
  useUpdateAcceptedCurrency,
} from "../../hooks/query/dposQueries";
import { useToast } from "../../hooks/use-toast";
import type { DposRequestError } from "../../services/dposService";

interface AcceptedCurrency {
  assetId: string;
  symbol: string;
  assetName: string;
  image: string | null;
  chain: string | null;
  isAccepted: boolean;
  businessWallet: {
    exists: boolean;
    address: string | null;
    fireblocksId: string | null;
    network: string | null;
    identifierEmail: string | null;
  };
}

interface POSReceivingAssetsProps {
  businessId: string;
  isPageLoading?: boolean;
  onCreatePayment: () => void;
  onCashierMode: () => void;
}

export default function POSReceivingAssets({
  businessId,
  isPageLoading = false,
  onCreatePayment,
  onCashierMode,
}: POSReceivingAssetsProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [optimisticValues, setOptimisticValues] = useState<
    Record<string, boolean>
  >({});
  const [pendingAssets, setPendingAssets] = useState<Set<string>>(new Set());
  const [walletCreationTarget, setWalletCreationTarget] =
    useState<AcceptedCurrency | null>(null);
  const [isConfirmingWalletCreation, setIsConfirmingWalletCreation] =
    useState(false);
  const { toast } = useToast();

  const { data: currencies = [], isLoading: isCurrenciesLoading } =
    useAcceptedCurrencies(businessId);
  const { mutate: toggleCurrency } = useUpdateAcceptedCurrency(businessId);

  const isLoading =
    isPageLoading || (isCurrenciesLoading && currencies.length === 0);

  // For each asset, find the chain's native token image by matching the chain
  // string against other currencies' symbols/names — no hardcoded list needed.
  const chainImageMap = useMemo(() => {
    const list = currencies as AcceptedCurrency[];
    const map: Record<string, string | null> = {};
    for (const c of list) {
      if (!c.chain) {
        map[c.assetId] = null;
        continue;
      }
      const chainLower = c.chain.toLowerCase();
      const native = list.find(
        (other) => other.assetName?.toLowerCase() === chainLower
      );
      map[c.assetId] = native?.image ?? null;
    }
    return map;
  }, [currencies]);

  const filteredCurrencies = useMemo(() => {
    if (!searchQuery.trim()) return currencies as AcceptedCurrency[];
    const query = searchQuery.toLowerCase();
    return (currencies as AcceptedCurrency[]).filter(
      (c) =>
        c.symbol?.toLowerCase().includes(query) ||
        c.assetName?.toLowerCase().includes(query)
    );
  }, [currencies, searchQuery]);

  const clearPendingState = (assetId: string) => {
    setPendingAssets((prev) => {
      const next = new Set(prev);
      next.delete(assetId);
      return next;
    });
  };

  const clearOptimisticState = (assetId: string) => {
    setOptimisticValues((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([key]) => key !== assetId)
      )
    );
  };

  const handleToggleError = (
    currency: AcceptedCurrency,
    currentState: boolean,
    error: unknown
  ) => {
    clearPendingState(currency.assetId);
    setOptimisticValues((prev) => ({
      ...prev,
      [currency.assetId]: currentState,
    }));
    setIsConfirmingWalletCreation(false);

    const requestError = error as DposRequestError;
    if (
      !currentState &&
      requestError?.data?.requiresWalletCreation &&
      !walletCreationTarget
    ) {
      clearOptimisticState(currency.assetId);
      setWalletCreationTarget(currency);
      return;
    }

    toast({
      description: requestError.message || "Failed to update currency",
      variant: "destructive",
    });
  };

  const submitToggle = (
    currency: AcceptedCurrency,
    currentState: boolean,
    createWalletIfMissing = false
  ) => {
    const newState = !currentState;
    setOptimisticValues((prev) => ({
      ...prev,
      [currency.assetId]: newState,
    }));
    setPendingAssets((prev) => new Set(prev).add(currency.assetId));

    toggleCurrency(
      {
        assetId: currency.assetId,
        isAccepted: newState,
        createWalletIfMissing,
      },
      {
        onSuccess: () => {
          clearPendingState(currency.assetId);
          clearOptimisticState(currency.assetId);
          setWalletCreationTarget(null);
          setIsConfirmingWalletCreation(false);
        },
        onError: (error: unknown) => {
          handleToggleError(currency, currentState, error);
        },
      }
    );
  };

  const handleToggle = (assetId: string, currentState: boolean) => {
    const currency = (currencies as AcceptedCurrency[]).find(
      (item) => item.assetId === assetId
    );

    if (!currency) {
      return;
    }

    const nextState = !currentState;
    if (nextState && !currency.businessWallet?.exists) {
      setWalletCreationTarget(currency);
      return;
    }

    submitToggle(currency, currentState);
  };

  const handleConfirmWalletCreation = () => {
    if (!walletCreationTarget) {
      return;
    }

    setIsConfirmingWalletCreation(true);
    submitToggle(walletCreationTarget, false, true);
  };

  return (
    <>
      <div className="bg-white border border-[#EBE9EF] rounded-[24px] flex flex-col gap-4 py-6 w-full h-full">
        <div className="flex flex-col gap-4 w-full">
          {/* Header with search */}
          <div className="flex items-center justify-between w-full px-6 gap-3">
            <h3 className="text-[18px] font-semibold leading-[28px] text-[#6D6584] shrink-0">
              POS receiving assets
            </h3>
            <div className="bg-[rgba(235,233,239,0.4)] rounded-full h-[44px] flex items-center pl-4 pr-4 gap-2 flex-1 max-w-[220px]">
              <Search size={13} className="text-[#6D6584] shrink-0" />
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent border-none outline-none text-[13px] text-[#6D6584] placeholder-[#9CA3AF] w-full"
              />
            </div>
          </div>

          {/* Asset list */}
          <div className="flex flex-col h-[342px] w-full overflow-y-auto px-6">
            <div className="flex flex-col gap-2">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="border border-[#F1EDF7] rounded-[16px] h-[72px] flex items-center justify-between px-4 animate-pulse"
                  >
                    <div className="flex gap-4 items-center">
                      <div className="w-8 h-8 rounded-full bg-gray-200" />
                      <div>
                        <div className="w-12 h-4 bg-gray-200 rounded mb-1" />
                        <div className="w-16 h-3 bg-gray-200 rounded" />
                      </div>
                    </div>
                    <div className="w-[52px] h-8 bg-gray-200 rounded-full" />
                  </div>
                ))
              ) : filteredCurrencies.length === 0 ? (
                <div className="flex items-center justify-center h-[200px] text-[14px] text-[#6D6584]">
                  {searchQuery ? "No assets found" : "No assets available"}
                </div>
              ) : (
                filteredCurrencies.map((currency) => (
                  <AssetRow
                    key={currency.assetId}
                    currency={currency}
                    checked={
                      optimisticValues[currency.assetId] ?? currency.isAccepted
                    }
                    onToggle={handleToggle}
                    chainImage={chainImageMap[currency.assetId] ?? null}
                    isPending={pendingAssets.has(currency.assetId)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      <Dialog
        open={Boolean(walletCreationTarget)}
        onOpenChange={(open) => {
          if (!open && !isConfirmingWalletCreation) {
            setWalletCreationTarget(null);
          }
        }}
      >
        <DialogContent className="max-w-[520px] rounded-[28px] border border-[#EBE9EF] bg-white p-0 overflow-hidden">
          <div className="p-7 sm:p-8">
            <DialogHeader className="gap-3 text-left">
              <DialogTitle className="text-[24px] font-semibold leading-[30px] text-[#2E2A3B]">
                {walletCreationTarget
                  ? `Create ${walletCreationTarget.symbol.toUpperCase()} wallet?`
                  : "Create wallet?"}
              </DialogTitle>
              <DialogDescription className="text-[15px] leading-[24px] text-[#6D6584]">
                {walletCreationTarget
                  ? `To receive ${walletCreationTarget.symbol.toUpperCase()} payments in DPOS, we'll create a dedicated ${
                      walletCreationTarget.chain
                        ? `${walletCreationTarget.chain} `
                        : ""
                    }${walletCreationTarget.assetName} wallet for this business and enable it automatically.`
                  : ""}
              </DialogDescription>
            </DialogHeader>

            <DialogFooter className="mt-7 flex-row gap-3 sm:justify-start sm:space-x-0">
              <Button
                type="button"
                variant="outline"
                className="h-11 rounded-full border-[#D9D4E3] px-5 text-[#544B68] hover:bg-[#F8F6FB]"
                onClick={() => setWalletCreationTarget(null)}
                disabled={isConfirmingWalletCreation}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="h-11 rounded-full bg-[#EB3366] px-5 text-white hover:bg-[#d92a5a]"
                onClick={handleConfirmWalletCreation}
                isLoading={isConfirmingWalletCreation}
              >
                Create Wallet & Enable
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AssetRow({
  currency,
  checked,
  onToggle,
  chainImage,
  isPending,
}: {
  currency: AcceptedCurrency;
  checked: boolean;
  onToggle: (assetId: string, currentState: boolean) => void;
  chainImage: string | null;
  isPending: boolean;
}) {
  return (
    <div className="border border-[#F1EDF7] rounded-[16px] h-[72px] flex items-center justify-between px-4">
      <div className="flex gap-4 items-center">
        {/* Token icon with chain overlay */}
        <div className="relative shrink-0 w-8 h-8">
          {currency.image ? (
            <img
              src={currency.image}
              alt={currency.symbol}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#EBE9EF] flex items-center justify-center text-[10px] font-bold text-[#6D6584]">
              {(currency.symbol || "?").slice(0, 2).toUpperCase()}
            </div>
          )}
          {chainImage && (
            <img
              src={chainImage}
              alt="chain"
              className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border border-white object-cover"
            />
          )}
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex gap-1 items-center">
            <span className="text-[16px] font-semibold leading-[24px] text-[#423E51]">
              {currency.symbol?.toUpperCase()}
            </span>
            {currency.chain && (
              <span className="bg-[rgba(235,233,239,0.3)] rounded-full px-2 py-[2px] text-[10px] font-bold tracking-[0.5px] uppercase text-[#6D6584]">
                {currency.chain}
              </span>
            )}
          </div>
          <span className="text-[12px] text-[#6D6584] leading-[14px]">
            {currency.assetName}
          </span>
        </div>
      </div>

      <Switch
        checked={checked}
        loading={isPending}
        onCheckedChange={() => onToggle(currency.assetId, checked)}
        className={cn(
          "h-8 w-[52px] [&>span]:h-6 [&>span]:w-6 [&>span]:data-[state=checked]:translate-x-[22px]",
          "data-[state=checked]:bg-[#EB3366] data-[state=unchecked]:bg-[#EBE9EF]"
        )}
      />
    </div>
  );
}
